import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fastSignOut } from "@/lib/sign-out";
import { useAuth } from "@/lib/auth-context";
import { useAccess } from "@/hooks/use-access";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { centsToDollars, dollarsToCents } from "@/lib/format";
import { createPortalSession } from "@/lib/payments.functions";
import { getStripeEnvironment, hasPaymentsConfigured } from "@/lib/stripe";
import { ExternalLink, PlayCircle } from "lucide-react";
import { TutorialCarousel, resetTutorial } from "@/components/tutorial-carousel";
import { IntegrationsCard } from "@/components/integrations-card";

export const Route = createFileRoute("/_authenticated/settings")({
  head: () => ({ meta: [{ title: "Settings — Boardroom" }] }),
  component: SettingsPage,
});

function SettingsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("*").eq("id", user!.id).maybeSingle();
      return data;
    },
  });

  const [name, setName] = useState("");
  const [anchor, setAnchor] = useState("");
  const [goal, setGoal] = useState("");
  const [ehr, setEhr] = useState("");
  const [hours, setHours] = useState("");
  const [taxPct, setTaxPct] = useState("");
  const [workDays, setWorkDays] = useState("");
  const [busy, setBusy] = useState(false);
  const [replayTutorial, setReplayTutorial] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setName(profile.full_name ?? "");
    setAnchor(profile.identity_anchor ?? "");
    setGoal(String(centsToDollars(profile.annual_goal_cents)));
    setEhr(String(centsToDollars(profile.target_ehr_cents)));
    setHours(String(profile.target_hours_per_week ?? 30));
    setTaxPct(String((profile as any).tax_reserve_pct ?? 0));
    setWorkDays(String((profile as any).working_days_per_month ?? 22));
  }, [profile?.id]);

  async function save() {
    if (!user) return;
    setBusy(true);
    const { error } = await supabase.from("profiles").update({
      full_name: name.trim(),
      identity_anchor: anchor.trim(),
      annual_goal_cents: dollarsToCents(goal || "0"),
      target_ehr_cents: dollarsToCents(ehr || "0"),
      target_hours_per_week: parseInt(hours) || 30,
      tax_reserve_pct: Math.max(0, Math.min(100, parseFloat(taxPct) || 0)),
      working_days_per_month: Math.max(1, Math.min(31, parseInt(workDays) || 22)),
    } as any).eq("id", user.id);
    setBusy(false);
    if (error) return toast.error(error.message);
    qc.invalidateQueries();
    toast.success("Settings saved");
  }

  async function signOut() {
    await fastSignOut();
  }

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h1 className="font-display text-3xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Your boardroom, configured.</p>
      </div>

      <Card title="Profile">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Email"><Input value={user?.email ?? ""} disabled className="opacity-60" /></Field>
        <Field label="Identity Anchor"><Textarea value={anchor} onChange={(e) => setAnchor(e.target.value)} rows={2} /></Field>
      </Card>

      <Card title="Targets">
        <Field label="Annual revenue goal">
          <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
            <Input type="number" value={goal} onChange={(e) => setGoal(e.target.value)} className="pl-7 tabular" /></div>
        </Field>
        <Field label="Target Effective Hourly Rate">
          <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
            <Input type="number" value={ehr} onChange={(e) => setEhr(e.target.value)} className="pl-7 tabular" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">/hr</span></div>
        </Field>
        <Field label="Target work hours per week"><Input type="number" value={hours} onChange={(e) => setHours(e.target.value)} className="tabular" /></Field>
      </Card>

      <Card title="Money mechanics">
        <Field label="Tax reserve %">
          <div className="relative">
            <Input type="number" min="0" max="100" step="0.5" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} className="pr-8 tabular" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Applied to this month's revenue on the Revenue page so you know what to set aside.</p>
        </Field>
        <Field label="Working days per month">
          <Input type="number" min="1" max="31" value={workDays} onChange={(e) => setWorkDays(e.target.value)} className="tabular" />
          <p className="text-xs text-muted-foreground mt-1">Used to calculate your Daily Cost of Trading — what you must earn each working day to cover recurring burn.</p>
        </Field>
      </Card>

      <IntegrationsCard />

      <BillingCard />

      <Card title="Help">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display text-sm font-medium">App tutorial</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Replay the quick tour of your boardroom.
            </p>
          </div>
          <Button
            variant="outline"
            className="border-[color:var(--hairline)] gap-2"
            onClick={() => {
              if (user) resetTutorial(user.id);
              setReplayTutorial(true);
            }}
          >
            <PlayCircle className="h-4 w-4" /> Replay tutorial
          </Button>
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={busy} className="bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">Save changes</Button>
        <Button variant="outline" onClick={signOut} className="border-[color:var(--hairline)]">Sign out</Button>
      </div>

      {user && replayTutorial && (
        <TutorialCarousel
          userId={user.id}
          force
          onClose={() => setReplayTutorial(false)}
        />
      )}
    </div>
  );
}

function BillingCard() {
  const access = useAccess();
  const [busy, setBusy] = useState(false);
  const paymentsReady = hasPaymentsConfigured();

  async function openPortal() {
    setBusy(true);
    try {
      const res = await createPortalSession({
        data: {
          returnUrl: `${window.location.origin}/settings`,
          environment: getStripeEnvironment(),
        },
      });
      if ("error" in res) throw new Error(res.error);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      toast.error(e.message ?? "Could not open billing portal");
    } finally {
      setBusy(false);
    }
  }

  const statusLabel: Record<string, string> = {
    trialing: "Free trial",
    active: "Active",
    comped: "Lifetime (comped)",
    past_due: "Payment past due",
    canceled: "Canceled",
    trial_expired: "Trial ended",
  };

  return (
    <Card title="Billing">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Status</div>
          <div className="font-display text-lg font-medium mt-0.5">
            {access.status ? statusLabel[access.status] ?? access.status : "—"}
          </div>
          {access.status === "trialing" && access.trialDaysLeft !== null && (
            <div className="text-xs text-muted-foreground mt-1">
              {access.trialDaysLeft} day{access.trialDaysLeft === 1 ? "" : "s"} remaining
            </div>
          )}
          {access.status === "active" && access.currentPeriodEnd && (
            <div className="text-xs text-muted-foreground mt-1">
              Renews {new Date(access.currentPeriodEnd).toLocaleDateString()}
            </div>
          )}
          {access.status === "canceled" && access.currentPeriodEnd && new Date(access.currentPeriodEnd) > new Date() && (
            <div className="text-xs text-muted-foreground mt-1">
              Access ends {new Date(access.currentPeriodEnd).toLocaleDateString()}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {access.status === "comped" ? (
            <span className="text-xs text-muted-foreground">Nothing to manage — you have lifetime access.</span>
          ) : ["active", "past_due", "canceled"].includes(access.status ?? "") ? (
            <Button
              onClick={openPortal}
              disabled={busy || !paymentsReady}
              variant="outline"
              className="border-[color:var(--hairline)] gap-2"
            >
              {busy ? "Opening…" : "Manage billing"}
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              onClick={() => (window.location.href = "/paywall")}
              className="bg-[color:var(--gold)] text-[color:var(--primary-foreground)]"
            >
              Start membership
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Billing is handled by Stripe. Manage billing opens Stripe's secure portal in a new tab to update your card, download invoices, or cancel.
      </p>
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="boardroom-card p-6 space-y-4">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><Label className="text-sm mb-1.5 block">{label}</Label>{children}</div>;
}
